import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../lib/language';
import { CompanyLogoAndNameState } from '../../lib/models/fundamental/types';
import { PositionType } from '../../lib/store/reporting/types';
import { hammerBasicColors } from '../../lib/util/chart-colors';

import useIsMobile from '../../shared_components/hooks/useIsMobile';
import { UI_ROUTES } from '../../util/routes';
import { ClickableDiv, CompanyLogo } from '../index';
import ButtonsCancelAndModify from '../TableView/tableContainer/ButtonsCancelAndModify/ButtonsCancelAndModify';
import ButtonsSellAndBuy from '../TableView/tableContainer/ButtonsSellAndBuy/ButtonsSellAndBuy';

import { SWIPE_DISTANCE_LIMIT } from './constants';

import styles from './MyAccountButtons.module.scss';

type Props = {
  data: any;
  companyLogoAndNameData: any;
  hasSymbol?: boolean;
  companyLogos?: boolean;
  pendingOrders?: boolean;
};

const DEFAULT_NAME_LENGTH = 16;

const MyAccountButtons = ({
  data,
  hasSymbol,
  pendingOrders,
  companyLogoAndNameData,
  companyLogos,
}: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [swipedRow, setSwipedRow] = useState<string>('');
  const wrapperRowRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  let touchStartX = 0;
  let touchEndX = 0;

  const handleClickRowMob = (symbolName: string, e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    navigate(`${UI_ROUTES.symbol}/${symbolName}`);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndX = e.touches[0].clientX;
  };

  const handleTouchEnd = (rowKey: string) => {
    if (touchStartX - touchEndX > SWIPE_DISTANCE_LIMIT) {
      setSwipedRow(rowKey);
    }

    if (touchStartX - touchEndX < -SWIPE_DISTANCE_LIMIT) {
      setSwipedRow('');
    }
  };

  const updateWidth = () => {
    if (wrapperRowRef.current) {
      setWidth(wrapperRowRef.current.offsetWidth);
    }
  };

  useLayoutEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const trimName = (name: string) => {
    if (!name) {
      return '';
    }

    if (name.length > DEFAULT_NAME_LENGTH) {
      return `${name.slice(0, DEFAULT_NAME_LENGTH)}...`;
    }
    return name;
  };

  return (
    <div>
      {data.map((row, index: number) => {
        let companyLogoAndName: CompanyLogoAndNameState | undefined;
        if (!hasSymbol) {
          companyLogoAndName = companyLogoAndNameData[row.name];
        }
        const rowKey = row.id;
        const wrapperRowClass = classNames(styles.wrapperRow, {
          [styles.swipedLeft]: swipedRow === rowKey,
        });
        return (
          <div key={rowKey} className={wrapperRowClass} ref={wrapperRowRef}>
            <div
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={() => handleTouchEnd(rowKey)}
              className={classNames(styles.row, { [styles.rowSwipped]: swipedRow === rowKey })}
            >
              <ClickableDiv
                className={styles.rowClickable}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClickRowMob(row.name, e)}
                style={{ width: `${width - 10}px` }}
              >
                <div
                  style={{ background: hammerBasicColors[index] }}
                  className={styles.colorVerticalLine}
                />
                <div className={styles.logo}>
                  {companyLogos ? (
                    <CompanyLogo
                      url={companyLogoAndName?.url}
                      logo={companyLogoAndName?.logo}
                      className={styles.image}
                      symbol={row.name}
                    />
                  ) : (
                    <img src={row.icon} className={styles.image} alt="" />
                  )}
                </div>
                <div className={styles.infoWrapper}>
                  <div className={styles.companyStocksAndPrice}>
                    <div className={styles.stocks}>
                      {pendingOrders ? (
                        <>{[row.totalQuantity, row.name, 'x', row.setPrice].join(' ')}</>
                      ) : (
                        <>
                          {[row.quantity, row.name, 'x', row.purchasePrice].join(' ')}
                          {row.positionType === PositionType.Sell && (
                            <span className={styles.positionType}>{` ${lang.commonShort()}`}</span>
                          )}
                        </>
                      )}
                    </div>
                    <div className={styles.expiryDate}>
                      {pendingOrders ? row.expiryDate : row.marketPrice}
                    </div>
                  </div>
                  <div className={styles.companyNameAndUnrealizedProfit}>
                    <div className={styles.companyName}>
                      {pendingOrders ? row.type : trimName(row.subtitle)}
                    </div>
                    {!pendingOrders && (
                      <div
                        className={classNames(styles.unrealizedProfit, {
                          [styles.positive]: row.unrealizedProfitNumber > 0,
                          [styles.negative]: row.unrealizedProfitNumber < 0,
                        })}
                      >
                        {row.unrealizedProfit}
                      </div>
                    )}
                  </div>
                </div>
              </ClickableDiv>
              {pendingOrders ? (
                <ButtonsCancelAndModify row={row} isSwipeable={isMobile} />
              ) : (
                <ButtonsSellAndBuy row={row} isSwipeable />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
export default MyAccountButtons;
